import type { CustomExperimentSpec } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import { safeStorage, createToggleRow } from '@learnforge/demo-kit';
import { createActionBar, type ActionBarButton } from '../action-bar';
import { mod, modAdd, modMul } from '../arith/modular';
import { createClock } from '../render/clock';
import { createSvgEl } from '@learnforge/demo-render';
import { MODULAR_PRESETS } from '../data/modular-presets';
import type { ArithContext, ArithKey } from '../types';

type ClockOp = '+' | '*';
const OP_SYMBOL: Record<ClockOp, string> = { '+': '+', '*': '×' };

export interface HandKeyframe {
  t: number;
  angle: number;
}

// Linear interpolation of the clock hand from `from` to `to` radians over
// `durationMs`, as an ordered list of (time, angle) samples for animation.
export function handAnimationKeyframes(
  from: number,
  to: number,
  durationMs: number,
  steps = 12,
): HandKeyframe[] {
  const frames: HandKeyframe[] = [];
  for (let i = 0; i <= steps; i += 1) {
    const progress = i / steps;
    frames.push({ t: progress * durationMs, angle: from + (to - from) * progress });
  }
  return frames;
}

// ============================================================================
// State
// ============================================================================

interface State {
  n: number;
  a: number;
  b: number;
  op: ClockOp;
  advanced: boolean;
}

// ============================================================================
// Spec factory
// ============================================================================

const ADVANCED_STORAGE_KEY = 'arithmetic.modular-clock.advanced';

function buildSpec(): CustomExperimentSpec<State, ArithKey, ArithContext> {
  return {
    labelKey: 'experiment.modular-clock.label',
    demoId: 'arithmetic',
    advancedKey: 'modular-clock',

    initialState(ctx): State {
      return {
        n: 12,
        a: 7,
        b: 8,
        op: '+',
        advanced: ctx.advanced,
      };
    },

    createRender(ctx, host, controls) {
      // ── DOM: SVG stage ──
      const svg = createSvgEl('svg', { viewBox: '0 0 360 410', class: 'arith-stage' });
      host.appendChild(svg);
      const clockGroup = createSvgEl('g', {});
      svg.appendChild(clockGroup);
      const formula = createSvgEl('text', {
        class: 'arith-readout',
        x: 180,
        y: 392,
        'text-anchor': 'middle',
      });
      svg.appendChild(formula);

      // Closure-only animation/render vars (NOT in State):
      let clockHandle: ReturnType<typeof createClock> | null = null;
      let animRafId: number | null = null;

      // ── DOM: Panel — operation buttons ──
      const OPS: ClockOp[] = ['+', '*'];
      const opRow = document.createElement('div');
      opRow.className = 'arith-op-row';
      const opButtons = new Map<ClockOp, HTMLButtonElement>();
      for (const op of OPS) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'arith-op-btn';
        btn.textContent = OP_SYMBOL[op];
        btn.addEventListener('click', () => {
          controls.setState((s) => ({ ...s, op }));
        });
        opButtons.set(op, btn);
        opRow.appendChild(btn);
      }
      ctx.panel.appendChild(opRow);

      function syncOpHighlight(op: ClockOp): void {
        for (const [o, btn] of opButtons) {
          btn.classList.toggle('active', o === op);
        }
      }

      // ── DOM: Panel — advanced number inputs (a / b / mod) ──
      const fieldInputs = new Map<'a' | 'b' | 'n', HTMLInputElement>();

      function makeField(which: 'a' | 'b' | 'n', label: string, min: number): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'arith-frac-input';
        const lbl = document.createElement('label');
        lbl.textContent = label;
        const input = document.createElement('input');
        input.type = 'number';
        input.min = String(min);
        input.step = '1';
        // value seeded in update() via guarded sync
        input.addEventListener('change', () => {
          const v = Math.floor(Number(input.value));
          if (Number.isFinite(v) && v >= min) {
            controls.setState((s) => ({ ...s, [which]: v }));
          }
        });
        fieldInputs.set(which, input);
        wrap.appendChild(lbl);
        wrap.appendChild(input);
        return wrap;
      }

      // Advanced fields container — appended to panel when advanced, removed when not.
      const advFieldsContainer = document.createElement('div');
      advFieldsContainer.appendChild(makeField('a', 'a', -99));
      advFieldsContainer.appendChild(makeField('b', 'b', -99));
      advFieldsContainer.appendChild(makeField('n', 'mod', 2));

      // ── DOM: Panel — advanced toggle ──
      const advToggle = createToggleRow({
        i18n: ctx.i18n,
        labelKey: 'btn.advanced',
        checked: ctx.advanced,
        onChange(next) {
          safeStorage.set(ADVANCED_STORAGE_KEY, next ? 'true' : 'false');
          controls.setState((s) => ({ ...s, advanced: next }));
        },
      });
      ctx.panel.appendChild(advToggle.element);

      // Track last advanced to detect show/hide transitions
      let advancedShown = false;

      // ── DOM: Action bar — built once ──
      const actionBar = createActionBar(
        ctx.actionBar,
        buildPresetButtons({ n: 12, a: 7, b: 8, op: '+', advanced: false }),
      );

      // ── Helpers ──
      function computeResult(s: State): number {
        return s.op === '+' ? modAdd(s.a, s.b, s.n) : modMul(s.a, s.b, s.n);
      }

      function drawClock(s: State, angle: number, showResult: boolean): void {
        clockHandle?.destroy();
        const highlighted = new Set<number>([mod(s.a, s.n), mod(s.b, s.n)]);
        if (showResult) highlighted.add(computeResult(s));
        clockHandle = createClock(clockGroup, {
          positions: s.n,
          highlighted,
          hand: { angle },
          width: 360,
          height: 360,
        });
      }

      function updateFormula(s: State): void {
        formula.textContent = `${s.a} ${OP_SYMBOL[s.op]} ${s.b} ≡ ${computeResult(s)} (mod ${s.n})`;
      }

      function buildPresetButtons(s: State): ActionBarButton[] {
        return MODULAR_PRESETS.map((p) => ({
          id: p.id,
          labelKey: `${p.a}${OP_SYMBOL[p.op]}${p.b} mod ${p.n}`,
          active: p.n === s.n && p.a === s.a && p.b === s.b && p.op === s.op,
          onClick() {
            controls.setState((prev) => ({
              ...prev,
              n: p.n,
              a: p.a,
              b: p.b,
              op: p.op,
            }));
          },
        }));
      }

      // ── Animation — uses controls.raf (NOT raw requestAnimationFrame) ──
      function startAnimation(s: State): void {
        // Cancel any in-progress animation.
        if (animRafId !== null) {
          controls.cancelRaf(animRafId);
          animRafId = null;
        }

        updateFormula(s);

        const totalSteps = s.op === '+' ? s.a + s.b : s.a * s.b;
        const targetAngle = (totalSteps / s.n) * Math.PI * 2;

        // Shot mode: skip animation, draw final frame directly.
        const noAnim =
          typeof requestAnimationFrame !== 'function' || document.body.hasAttribute('data-shot');
        if (noAnim) {
          drawClock(s, targetAngle, true);
          return;
        }

        const duration = 900;
        let startTs: number | null = null;

        // Capture a stable snapshot of the state values needed by the animation;
        // the closure should not read `s` captured here — it's already a snapshot.
        const animState = s;

        const tick = (ts: number): void => {
          if (startTs === null) startTs = ts;
          const t = Math.min(1, (ts - startTs) / duration);
          const eased = 1 - (1 - t) * (1 - t);
          drawClock(animState, eased * targetAngle, t >= 1);
          if (t < 1) {
            animRafId = controls.raf(tick);
          } else {
            animRafId = null;
          }
        };

        drawClock(animState, 0, false);
        animRafId = controls.raf(tick);
      }

      // ── RenderHandle.update — called by harness on every state change ──
      const update = (state: State): void => {
        // Sync op buttons
        syncOpHighlight(state.op);

        // Show/hide advanced fields (attach/detach from panel)
        if (state.advanced !== advancedShown) {
          advancedShown = state.advanced;
          if (state.advanced) {
            // Insert before the toggle row
            ctx.panel.insertBefore(advFieldsContainer, advToggle.element);
          } else {
            if (advFieldsContainer.parentNode === ctx.panel) {
              ctx.panel.removeChild(advFieldsContainer);
            }
          }
        }

        // Guarded number-input value sync (only when visible)
        if (state.advanced) {
          for (const [which, input] of fieldInputs) {
            const newVal = String(state[which]);
            if (input.value !== newVal) {
              input.value = newVal;
            }
          }
        }

        // Refresh preset buttons (active state may have changed)
        actionBar.setButtons(buildPresetButtons(state));

        // Trigger the sweep animation
        startAnimation(state);
      };

      // ── RenderHandle.dispose ──
      const dispose = (): void => {
        if (animRafId !== null) {
          controls.cancelRaf(animRafId);
          animRafId = null;
        }
        clockHandle?.destroy();
        clockHandle = null;
        actionBar.destroy();
      };

      return { update, dispose };
    },

    applyShotState(state): State {
      // data-shot check inside startAnimation inhibits the rAF sweep;
      // no state change needed.
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Backward-compatible alias: index.ts imports this by name.
export const mountModularClock = experiment.mount.bind(experiment);
